import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MenuItem } from 'primeng/api'
import { ActivatedRoute } from '@angular/router'
import { combineLatest, Subscription } from 'rxjs'
import { finalize, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { ApplicationResourceDto, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { maxDate, shortTimeString } from 'src/app/shared/utils'
import { handleArpioError } from 'src/utils/handle-error'
import { ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { Application, ApplicationProviderService } from '../../services/application.service'
import { RaisedIssue } from '../../services/issue'
import { IssuesViewComponent, ViewableIssue } from '../issues-view/issues-view.component'
import { AccessLevel, AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export enum ApplicationStatusState {
  INDETERMINATE,
  SUCCESS,
  ERROR,
}

export let backupStatusVals: { [key: string]: [ApplicationStatusState, string] } = {
  noneYet: [ApplicationStatusState.INDETERMINATE, 'No recovery points have been created yet'],
  creatingInitial: [ApplicationStatusState.INDETERMINATE, 'Creating initial recovery point'],
  creatingNew: [ApplicationStatusState.INDETERMINATE, 'Creating new recovery point'],
  failed: [ApplicationStatusState.ERROR, 'Creating recovery point failed (see issues below)'],
  succeeded: [ApplicationStatusState.SUCCESS, 'Successfully updated with data from {rpo_age}'],
}

export let restoreStatusVals: { [key: string]: [ApplicationStatusState, string] } = {
  awaiting: [ApplicationStatusState.INDETERMINATE, 'Awaiting recovery point creation'],
  notYet: [ApplicationStatusState.INDETERMINATE, 'Recovery environment has not yet been setup'],
  settingUp: [ApplicationStatusState.INDETERMINATE, 'Setting up the recovery environment'],
  updating: [ApplicationStatusState.INDETERMINATE, 'Updating the recovery environment'],
  failed: [ApplicationStatusState.ERROR, 'Failed update to recovery point from {rp_time} (see issues below)'],
  succeeded: [ApplicationStatusState.SUCCESS, 'Successfully updated with recovery point from {rp_time}.'],
  succeededWithoutRP: [ApplicationStatusState.SUCCESS, 'Successfully updated.'],
  unhandled: [ApplicationStatusState.ERROR, 'Please contact support.'],
  failedWithoutRP: [ApplicationStatusState.ERROR, 'Failed update to recovery point (see issues below)'],
}

export enum ProcessStatus {
  NEVER_ATTEMPTED = 'never attempted',
  FIRST_ATTEMPT_IN_PROGRESS = 'first attempt in progress',
  SUBSEQUENT_ATTEMPT_IN_PROGRESS = 'subsequent attempt in progress',
  MOST_RECENT_FAILED = 'most recent failed',
  MOST_RECENT_SUCCEEDED = 'most rececent succeeded',
}

function computeProcessStatus(
  lastProcessRequest: Date,
  lastProcessStart: Date,
  lastProcessSuccess: Date,
  lastProcessFailure: Date
) {
  const timeThresholdMinutes: number = 10 // 10 mins threshold
  const now = new Date()
  const timeThreshold = new Date(now.getTime() - timeThresholdMinutes * 60 * 1000)

  // Ignore lastProcessRequest if it's older than the threshold
  if (lastProcessRequest && lastProcessRequest < timeThreshold) {
    lastProcessRequest = null
  }

  const mostRecentProcess = maxDate(lastProcessSuccess, lastProcessFailure)

  if (!lastProcessRequest && !lastProcessStart) return ProcessStatus.NEVER_ATTEMPTED

  if (!lastProcessSuccess && !lastProcessFailure) return ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS

  // Check if there's a recent request or start that's newer than the last completion
  if (lastProcessRequest > mostRecentProcess || lastProcessStart > mostRecentProcess)
    return ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS

  if (lastProcessFailure > lastProcessStart) return ProcessStatus.MOST_RECENT_FAILED

  if (lastProcessStart < mostRecentProcess) {
    return lastProcessSuccess > lastProcessFailure
      ? ProcessStatus.MOST_RECENT_SUCCEEDED
      : ProcessStatus.MOST_RECENT_FAILED
  }
  return ProcessStatus.MOST_RECENT_SUCCEEDED
}

export function computeBackupStatusText(backupStatus: ProcessStatus, lastBackupStart: Date) {
  let backupStatusDetails: [ApplicationStatusState, string]

  switch (backupStatus) {
    case ProcessStatus.NEVER_ATTEMPTED:
      backupStatusDetails = backupStatusVals.noneYet
      break
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      backupStatusDetails = backupStatusVals.creatingInitial
      break
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
      backupStatusDetails = backupStatusVals.creatingNew
      break
    case ProcessStatus.MOST_RECENT_FAILED:
      backupStatusDetails = backupStatusVals.failed
      break
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      backupStatusDetails = backupStatusVals.succeeded
      break
  }

  backupStatusDetails = [
    backupStatusDetails[0],
    backupStatusDetails[1].replace('{rpo_age}', shortTimeString(lastBackupStart)),
  ]
  return backupStatusDetails
}

export function computeRestoreStatusText(
  backupStatus: ProcessStatus,
  restoreStatus: ProcessStatus,
  recoveryPointDate?: Date
) {
  let restoreStatusDetails: [ApplicationStatusState, string]

  switch (restoreStatus) {
    case ProcessStatus.NEVER_ATTEMPTED:
      switch (backupStatus) {
        case ProcessStatus.NEVER_ATTEMPTED:
        case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
          restoreStatusDetails = restoreStatusVals.awaiting
          break
        default:
          restoreStatusDetails = restoreStatusVals.notYet
      }
      break
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      restoreStatusDetails = restoreStatusVals.settingUp
      break
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
      restoreStatusDetails = restoreStatusVals.updating
      break

    case ProcessStatus.MOST_RECENT_FAILED:
      restoreStatusDetails = recoveryPointDate ? restoreStatusVals.failed : restoreStatusVals.failedWithoutRP // if it succeeds but does not have a recovery point, give it a different message
      break

    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      restoreStatusDetails = recoveryPointDate ? restoreStatusVals.succeeded : restoreStatusVals.succeededWithoutRP // if it fails but does not have a recovery point, give it a different message
      break

    default:
      restoreStatusDetails = restoreStatusVals.unhandled // Optional: safety for unexpected values (throw new Error("Unhandled restore status")
  }

  if (!!recoveryPointDate) {
    restoreStatusDetails = [
      restoreStatusDetails[0],
      restoreStatusDetails[1].replace('{rp_time}', shortTimeString(recoveryPointDate)),
    ]
  }

  return restoreStatusDetails
}

function makeViewableIssues(issues: Array<RaisedIssue>, resources: ApplicationResourceDto[]) {
  const viewableIssues = issues.map((raisedIssue) => {
    let resource = resources.find((res) => res.arn === raisedIssue.arn)
    let resourceType: ResourceType = null
    let resourceVariant: ResourceVariant = null
    let resourceName: string = null
    if (resource) {
      resourceType = resource.type
      resourceVariant = resource.variant
      resourceName = resource.source?.displayName || resource.source?.name || ''
    }

    return {
      ...raisedIssue,
      resourceName,
      resourceType,
      resourceVariant,
      arn: raisedIssue.arn || '', // Ensure arn is never null or undefined
      timestamp: raisedIssue.timestamp,
    } as ViewableIssue
  })

  // Sort by resourceType first, then by arn, and finally by timestamp for issues without resource info
  return viewableIssues.sort((a, b) => {
    // First, compare resourceType
    const resourceTypeComparison = (a.resourceType || '').localeCompare(b.resourceType || '') || 0
    if (resourceTypeComparison !== 0) {
      return resourceTypeComparison
    }

    // If resourceType is the same, compare arn
    const arnComparison = (a.arn || '').localeCompare(b.arn || '')
    if (arnComparison !== 0) {
      return arnComparison
    }

    // If both resourceType and arn are the same or missing, fallback to timestamp comparison
    return a.timestamp?.getTime() - b.timestamp?.getTime()
  })
}

@Component({
  selector: 'application-issues',
  templateUrl: './application-issues.component.html',
  styleUrls: ['./application-issues.component.scss'],
  standalone: false,
})
export class ApplicationIssuesComponent implements OnInit, OnDestroy {
  @ViewChild('backupIssuesView') backupIssuesView: IssuesViewComponent
  @ViewChild('restoreIssuesView') restoreIssuesView: IssuesViewComponent
  @ViewChild('failbackIssuesView') failbackIssuesView: IssuesViewComponent

  private applicationIdSubscription: Subscription = null
  private applicationSubscription: Subscription = null
  private backupSubscription: Subscription = null
  private restoreSubscription: Subscription = null
  private failbackSubscription: Subscription = null

  private _application: Application = null
  private _resources: ApplicationResourceDto[] = null

  private _hasEverHadBackupIssues = false
  private _hasEverHadRestoreIssues = false
  private _hasEverHadFailbackIssues = false

  private _launchingBackup = false
  private _launchingRestore = false

  // Permission properties
  private canTestPermission = false
  private canManagePermission = false
  private canRecoverPermission = false
  private permissionsSubscription: Subscription = null

  restoreMenuItems: MenuItem[] = [
    {
      label: 'Resume Recovery<div class="menu-desc">Pick up the restore from the point of failure</div>',
      escape: false,
      command: () => this.launchRestoreToTryAgain(true),
    },
    {
      label: 'Retry Recovery<div class="menu-desc">Restart the entire restore from the beginning</div>',
      escape: false,
      command: () => this.launchRestoreToTryAgain(false),
    },
  ]
  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private apiService: ApiService,
    private uiService: UiService,
    private accessLevelService: AccessLevelService
  ) {}

  ngOnInit(): void {
    this.applicationIdSubscription = combineLatest([this.accountService.accountIdStream, this.route.paramMap])
      .pipe(finalize(() => (this.applicationIdSubscription = null)))
      .subscribe(([_accountId, paramMap]) => this.initializeApplication(paramMap.get('applicationId')))
  }

  ngOnDestroy(): void {
    this.cleanup()

    // Don't unsubscribe to this one in the cleanup() method -- that'll cancel it whenever we
    // change applications
    if (subscriptionActive(this.applicationIdSubscription)) {
      this.applicationIdSubscription.unsubscribe()
      this.applicationIdSubscription = null
    }

    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }
  }

  private cleanup() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }

    if (subscriptionActive(this.backupSubscription)) {
      this.backupSubscription.unsubscribe()
      this.backupSubscription = null
    }

    if (subscriptionActive(this.restoreSubscription)) {
      this.restoreSubscription.unsubscribe()
      this.restoreSubscription = null
    }

    if (subscriptionActive(this.failbackSubscription)) {
      this.failbackSubscription.unsubscribe()
      this.failbackSubscription = null
    }
  }

  private initializeApplication(applicationId: string) {
    // Cleanup any existing state from prior application
    this.cleanup()

    this._hasEverHadBackupIssues = false
    this._hasEverHadRestoreIssues = false
    this._hasEverHadFailbackIssues = false

    // Load the application
    this.applicationSubscription = this.applicationProviderService
      .loadApplication$(applicationId, this.accountService.accountId)
      .pipe(finalize(() => (this.applicationSubscription = null)))
      .subscribe((application) => {
        this.setApplication(application)
      })
  }

  private setApplication(application: Application) {
    this._application = application
    this._resources = application.resources
    if (!!this._application.backupIssues && this._application.backupIssues.length > 0) {
      this._hasEverHadBackupIssues = true
    }

    if (!!this._application.restoreIssues && this._application.restoreIssues.length > 0) {
      this._hasEverHadRestoreIssues = true
    }

    if (!!this._application.failbackIssues && this._application.failbackIssues.length > 0) {
      this._hasEverHadFailbackIssues = true
    }

    // Load permissions for this specific application
    this.loadApplicationPermissions()
  }

  private loadApplicationPermissions() {
    if (!this._application) return

    // Clean up previous subscription
    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }

    // Load permissions for this specific application using the new permission-based methods
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAppPermission(this._application.applicationId, AppPermission.MANAGE_TEST),
      this.accessLevelService.hasAppPermission(this._application.applicationId, AppPermission.UPDATE_APP),
      this.accessLevelService.hasAppPermission(this._application.applicationId, AppPermission.MANAGE_RECOVERY),
    ]).subscribe(([canTest, canManage, canRecover]) => {
      this.canTestPermission = canTest
      this.canManagePermission = canManage
      this.canRecoverPermission = canRecover
    })
  }

  public computeBackupStatus() {
    let processStatus = computeProcessStatus(
      this._launchingBackup ? new Date() : this._application.lastBackupRequest,
      this._application.lastBackupStart,
      this._application.lastBackupSuccess,
      this._application.lastBackupFailure
    )

    let [status, text] = computeBackupStatusText(processStatus, this._application.lastBackupStart)

    let statusClass
    switch (status) {
      case ApplicationStatusState.SUCCESS:
        statusClass = 'success'
        break
      case ApplicationStatusState.ERROR:
        statusClass = 'error'
        break
      case ApplicationStatusState.INDETERMINATE:
        statusClass = 'indeterminate'
        break
    }

    return {
      class: statusClass,
      text: text,
    }
  }

  public computeRestoreStatus() {
    const backupStatus = computeProcessStatus(
      this._launchingBackup ? new Date() : this._application.lastBackupRequest,
      this._application.lastBackupStart,
      this._application.lastBackupSuccess,
      this._application.lastBackupFailure
    )
    const restoreStatus = computeProcessStatus(
      this._launchingRestore ? new Date() : this._application.lastRestoreRequest,
      this._application.lastRestoreStart,
      this._application.lastRestoreSuccess,
      this._application.lastRestoreFailure
    )

    const [status, text] = computeRestoreStatusText(
      backupStatus,
      restoreStatus,
      !!this._application.currentRecoveryPoint ? this._application.currentRecoveryPoint.timestamp : null
    )

    let statusClass
    switch (status) {
      case ApplicationStatusState.SUCCESS:
        statusClass = 'success'
        break
      case ApplicationStatusState.ERROR:
        statusClass = 'error'
        break
      case ApplicationStatusState.INDETERMINATE:
        statusClass = 'indeterminate'
        break
    }

    return {
      class: statusClass,
      text: text,
    }
  }

  public get applicationLoaded() {
    return !!this._application && this._application.isLoaded && this._resources
  }

  public get application() {
    return this._application
  }

  public get applicationName() {
    return this._application.name
  }

  private get resources() {
    return this._resources || []
  }

  public get backingUp() {
    return this._launchingBackup || this._application.currentlyBackingUp
  }

  get canTest(): boolean {
    return this.canTestPermission
  }

  get canManage(): boolean {
    return this.canManagePermission
  }

  get canRecover(): boolean {
    return this.canRecoverPermission
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  getRestorePermission(): boolean {
    if (!this._application) return false

    // Check sync phase to determine required permission
    if (this._application.syncPhase === SyncPhaseDto.FAILOVER) {
      // Failover scenario requires app_recover permission
      return this.canRecover
    } else if (this._application.syncPhase === SyncPhaseDto.FAILOVER_TEST) {
      // FailoverTest scenario requires app_test permission
      return this.canTest
    }

    // Default fallback - require both permissions
    return this.canTest && this.canRecover
  }

  public get backupIssues() {
    return makeViewableIssues((this._application && this._application.recoveryPointIssues) || [], this.resources)
  }

  public get hasEverHadBackupIssues() {
    return this._hasEverHadBackupIssues
  }

  public launchBackupToTryAgain() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }

    this._launchingBackup = true
    this.applicationSubscription = this._application
      .initiateBackup$()
      .pipe(tap((_) => (this._launchingBackup = false)))
      .subscribe(
        (application) => this.setApplication(application),
        (error) => {
          this._launchingBackup = false
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  public get restoring() {
    return this._launchingRestore || this._application.currentlyRestoring
  }

  public get restoreIssues() {
    return makeViewableIssues((this._application && this._application.restoreIssues) || [], this.resources)
  }

  public get hasEverHadRestoreIssues() {
    return this._hasEverHadRestoreIssues
  }

  public get canTryAgainFromJob() {
    // if resumeRecoveryStatus is populated, use the "available" field, otherwise assume it is available
    // for backward-compatibility
    const resumeRecoveryStatus = this._application.dto.resumeRecoveryStatus
      ? this._application.dto.resumeRecoveryStatus.available
      : true

    return (
      this.accountService.jobsTryAgainEnabled && this._application.dto.restoreStatus?.lastJobId && resumeRecoveryStatus
    )
  }

  public get resumeRecoveryUnavailableCause(): string | null {
    if (!this.accountService.jobsTryAgainEnabled) return null
    const status = this._application.dto.resumeRecoveryStatus
    if (!status || status.available) return null
    if (!status.cause) return null
    return 'Resume recovery is not available with the current recovery environment issue(s).'
  }

  public launchRestoreToTryAgain(jobsTryAgain: boolean = true) {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }

    this._launchingRestore = true

    let restoreObservable
    if (jobsTryAgain && this.canTryAgainFromJob) {
      restoreObservable = this._application.initiateRestoreFromJob$()
    } else {
      // If the app is deleted, use the latest recovery point for the retry, instead of the last one.
      // This will force the empty recovery point to be applied even if there are other apps in the
      // same region pair that are not in standby.
      const useLatestRecoveryPoint = this._application.deleted
      restoreObservable = this._application.initiateRestore$(useLatestRecoveryPoint)
    }

    this.applicationSubscription = restoreObservable.pipe(tap((_) => (this._launchingRestore = false))).subscribe(
      (application) => this.setApplication(application),
      (error) => {
        this._launchingRestore = false
        handleArpioError(window.location.href, this.apiService, this.uiService)(error)
      }
    )
  }

  public get failingBack() {
    return this._application.currentlyFailingBack
  }

  public get failbackIssues() {
    return makeViewableIssues((this._application && this._application.failbackIssues) || [], this.resources)
  }

  public get hasEverHadFailbackIssues() {
    return this._hasEverHadFailbackIssues
  }
}
